import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { compoundFactor } from '@shared/calculations/funding/projection/sharedPolicyConfig';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, ForwardTotalTooltip, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { reimbursementPayoutRows } from './payoutBreakdown';
import type { StrategyPresentation } from './types';

type AssetCareDeltas = {
  indemnitySurplusApplied?: number;
};

const hasAny = (timeline: AccumulationYear[], pick: (row: AccumulationYear) => number): boolean =>
  timeline.some(row => pick(row) > 0.5);

const aobPoolValue = (row: AccumulationYear): number =>
  Math.max(row.snapshot.theoreticalLtcPoolCeiling - row.snapshot.effectiveContinuationBenefit, 0);

function assetCareInflationTooltip({
  header,
  startingLabel,
  startingValue,
  rate,
  capYears,
  policyYear,
  result,
  compoundLabel,
}: {
  header: string;
  startingLabel: string;
  startingValue: number;
  rate: number;
  capYears: number | undefined;
  policyYear: number;
  result: number;
  compoundLabel: string;
}) {
  const cap = capYears && capYears > 0 ? capYears : null;
  const intYear = Math.floor(policyYear);
  const rawIncrements = Math.max(intYear - 1, 0);
  const years = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && rawIncrements >= cap;

  return (
    <InflationTooltipContent
      header={header}
      startingLabel={startingLabel}
      startingValue={startingValue}
      rate={rate}
      years={years}
      result={result}
      note={capped ? `Growth stops after ${cap} annual increases` : undefined}
      compoundLabel={compoundLabel}
    />
  );
}

function AssetCareStaticValueTooltip({
  header,
  startingLabel,
  startingValue,
  result,
  driver,
}: {
  header: string;
  startingLabel: string;
  startingValue: number;
  result: number;
  driver: string;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">{header}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        <span className="text-gray-300">Driver</span>
        <span className="font-semibold">{driver}</span>
      </div>
    </TooltipContent>
  );
}

// Renders a static "$Y/mo × Z mo = $X" note for a benefit-period-derived pool.
// Returns undefined for legacy policies where the benefit period is not known.
function poolAtIssueNote(
  baseMonthly: number,
  durationMonths: number | undefined,
  atIssue: number | undefined,
): string | undefined {
  if (durationMonths === undefined || atIssue === undefined) return undefined;
  return `At issue: ${currency(baseMonthly)}/mo × ${durationMonths} mo = ${currency(atIssue)}`;
}

function buildOaAssetCareAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  const firstRow = timeline[0];
  const aobAtIssue = terms.baseBenefitPoolAtIssue ?? (firstRow ? aobPoolValue(firstRow) : 0);
  const cobAtIssue =
    terms.extensionOfBenefitsPoolAtIssue ?? timeline[0]?.snapshot.effectiveContinuationBenefit ?? 0;
  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const cobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const cobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
  // Benefit-period (duration) model: pools = monthly × benefit-period months.
  // When active the AOB/COB columns are sized off the monthly benefit (not the
  // raw Face Amount), so the labels/formulas describe the derivation honestly.
  const accelMonths = terms.baseBenefitPoolDurationMonths;
  const cobMonths = terms.extensionOfBenefitsPoolDurationMonths;
  const usesDuration = accelMonths != null;
  const aobAtIssueLabel = usesDuration
    ? `AOB at issue (${currency(baseMonthly)}/mo × ${accelMonths} mo)`
    : 'Face Amount at issue';
  const cobAtIssueLabel =
    usesDuration && cobMonths != null
      ? `COB at issue (${currency(baseMonthly)}/mo × ${cobMonths} mo)`
      : 'Continuation Benefit Amount at issue';

  if (hasAny(timeline, aobPoolValue)) {
    columns.push({
      key: 'aob-pool',
      label: 'AOB pool',
      tooltip: (
        <HeaderTooltip
          description={
            usesDuration
              ? 'Acceleration of Benefits (AOB) dollars available before any LTC benefits are used. Sized as the AOB monthly benefit × the AOB benefit-period months, and grows as that monthly benefit inflates on the AOB rate.'
              : 'Acceleration of Benefits (AOB) dollars available before any LTC benefits are used. Starts from the Face Amount / Maximum Benefit Amount and grows on the AOB inflation rate.'
          }
          formulas={[
            {
              label: 'AOB pool',
              expression: usesDuration ? (
                <>
                  AOB monthly × {accelMonths} mo ×{' '}
                  <GrowthFactor rate="AOB rate" exponent="min(policy year − 1, cap years)" />
                </>
              ) : (
                <>
                  Face Amount ×{' '}
                  <GrowthFactor
                    rate="AOB rate"
                    exponent="min(policy year − 1, cap years)"
                  />
                </>
              ),
            },
          ]}
          notes="If a separate AOB rate is not listed, the policy-wide LTC inflation rate drives this value."
        />
      ),
      render: (row, prev) => {
        const value = aobPoolValue(row);
        return (
          <GrowthValueCell
            value={value}
            prevValue={prev ? aobPoolValue(prev) : null}
            tooltip={assetCareInflationTooltip({
              header: `Policy year ${row.policyYear} · AOB pool`,
              startingLabel: aobAtIssueLabel,
              startingValue: aobAtIssue,
              rate: baseRate,
              capYears: baseCapYears,
              policyYear: row.policyYear,
              result: value,
              compoundLabel: 'AOB anniversary growth',
            })}
          />
        );
      },
    });
  }

  if (hasAny(timeline, row => row.snapshot.effectiveContinuationBenefit)) {
    // Unlimited/Lifetime COB: the pool holds the sentinel and never grows or
    // depletes — a static "Unlimited" beats a raw ~$1B figure with a growth
    // tooltip (mirrors hybridLifeIra).
    const cobUnlimited = terms.isUnlimited === true;
    columns.push({
      key: 'cob-pool',
      label: 'COB pool',
      tooltip: (
        <HeaderTooltip
          description={
            usesDuration
              ? 'Continuation of Benefits (COB) dollars available after AOB is exhausted. Sized as the monthly benefit × the COB benefit-period months, and grows as that monthly benefit inflates on the COB rate.'
              : 'Continuation of Benefits (COB) dollars available after AOB is exhausted. Starts from the Continuation Benefit Amount and grows on the COB inflation rate.'
          }
          formulas={[
            {
              label: 'COB pool',
              expression:
                usesDuration && cobMonths != null ? (
                  <>
                    monthly × {cobMonths} mo ×{' '}
                    <GrowthFactor rate="COB rate" exponent="min(policy year − 1, cap years)" />
                  </>
                ) : (
                  <>
                    Continuation Benefit Amount ×{' '}
                    <GrowthFactor
                      rate="COB rate"
                      exponent="min(policy year − 1, cap years)"
                    />
                  </>
                ),
            },
          ]}
          notes="If a separate COB rate is not listed, the policy-wide LTC inflation rate drives this value. A policy with an unlimited Continuation Benefit has a lifetime COB pool that never depletes."
        />
      ),
      render: (row, prev) =>
        cobUnlimited ? (
          <div className="font-medium text-gray-900">Unlimited</div>
        ) : (
          <GrowthValueCell
            value={row.snapshot.effectiveContinuationBenefit}
            prevValue={prev?.snapshot.effectiveContinuationBenefit ?? null}
            tooltip={assetCareInflationTooltip({
              header: `Policy year ${row.policyYear} · COB pool`,
              startingLabel: cobAtIssueLabel,
              startingValue: cobAtIssue,
              rate: cobRate,
              capYears: cobCapYears,
              policyYear: row.policyYear,
              result: row.snapshot.effectiveContinuationBenefit,
              compoundLabel: 'COB anniversary growth',
            })}
          />
        ),
    });
  }

  // Forward-summed Total LTC Benefit — only under the benefit-period model
  // (the snapshot carries `forwardLtcBenefitTotal`). Distinct from the AOB/COB
  // pool columns above: those are the principal a claim opens with this year,
  // while this is the total paid over the whole benefit period as the monthly
  // benefit keeps inflating during the claim. The two converge at the cap year.
  if (hasAny(timeline, row => row.snapshot.forwardLtcBenefitTotal ?? 0)) {
    columns.push({
      key: 'total-ltc-benefit',
      label: 'Total LTC benefit',
      tooltip: (
        <HeaderTooltip
          description="Total long-term care dollars the policy would pay for a claim STARTING this policy year, summed across the full AOB then COB benefit period as the monthly benefit keeps inflating during the claim. This mirrors the carrier illustration's 'Total LTC Benefit' column."
          formulas={[
            {
              label: 'Total LTC benefit',
              expression:
                'Σ (monthly benefit × inflation) over the AOB then COB benefit-period months',
            },
          ]}
          notes="Exceeds the current AOB + COB pool because the monthly benefit keeps growing with inflation while the claim is being paid; the two converge once inflation reaches its cap year."
        />
      ),
      render: (row, prev) => {
        const value = row.snapshot.forwardLtcBenefitTotal ?? 0;
        return (
          <GrowthValueCell
            value={value}
            prevValue={prev?.snapshot.forwardLtcBenefitTotal ?? null}
            tooltip={
              <ForwardTotalTooltip
                policyYear={row.policyYear}
                principal={row.snapshot.theoreticalLtcPoolCeiling}
                total={value}
                headingPrefix="Policy year"
                description={
                  <>
                    Total dollars the policy pays for a claim starting this year, across the full
                    AOB + COB benefit period.
                  </>
                }
                poolLabel="AOB + COB"
              />
            }
          />
        );
      },
    });
  }

  if (hasAny(timeline, row => row.snapshot.monthlyBenefitCap)) {
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="AOB monthly LTC benefit limit before care begins. Starts from the policy's highest configured daily LTC benefit times 30 and grows on the AOB inflation rate."
          formulas={[
            {
              label: 'Monthly benefit',
              expression: (
                <>
                  max daily LTC benefit × 30 ×{' '}
                  <GrowthFactor
                    rate="AOB rate"
                    exponent="min(policy year − 1, cap years)"
                  />
                </>
              ),
            },
          ]}
          notes="After AOB is exhausted during care, COB rows use the COB-grown monthly limit."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={assetCareInflationTooltip({
            header: `Policy year ${row.policyYear} · AOB monthly benefit`,
            startingLabel: 'At-issue monthly maximum',
            startingValue: baseMonthly,
            rate: baseRate,
            capYears: baseCapYears,
            policyYear: row.policyYear,
            result: row.snapshot.monthlyBenefitCap,
            compoundLabel: 'AOB anniversary growth',
          })}
        />
      ),
    });
  }

  if (hasAny(timeline, row => row.snapshot.effectiveFaceAmount)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Life-insurance death benefit before any LTC benefits are used. Asset Care keeps this at the Face Amount before care begins."
          formulas={[{ label: 'Death benefit', expression: 'Face Amount before LTC use' }]}
          notes="During care, AOB payments reduce the death benefit in proportion to the remaining AOB pool; COB payments do not."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <AssetCareStaticValueTooltip
              header={`Policy year ${row.policyYear} · death benefit`}
              startingLabel="Face Amount"
              startingValue={row.snapshot.effectiveFaceAmount}
              result={row.snapshot.effectiveFaceAmount}
              driver="Face Amount; no pre-care inflation; AOB use later reduces it proportionally, COB use does not"
            />
          }
        />
      ),
    });
  }

  return columns;
}

export const oaAssetCarePresentation: StrategyPresentation = {
  accumulationColumns: buildOaAssetCareAccumulationColumns,

  // Asset Care's monthly cap is per care environment and per-pool (the cap
  // follows the COB rate once AOB is exhausted — the carrier's separate
  // AOB/COB Monthly Benefit Limit columns), so the cell headlines the
  // engine's own applied cap; the tooltip's base × (1+r)^n derivation is
  // reconstructed from terms for display only.
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears, fromEob } = appliedCapTooltipInputs(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `compoundFactor` so the tooltip's base × (1+r)^n equals the displayed result.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    const poolLabel = fromEob ? 'COB' : 'AOB';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear} · ${poolLabel} monthly limit`}
          startingLabel={`At issue (${poolLabel})`}
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `${poolLabel} inflation capped at year ${cap}` : undefined}
          compoundLabel="Compound growth"
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    // Under the benefit-period model, show each pool's "$X/mo × N mo" sizing so
    // the running balance reconciles with the monthly benefit it was sized from.
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    const baseAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.baseBenefitPoolDurationMonths,
      terms.baseBenefitPoolAtIssue,
    );
    const eobAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.extensionOfBenefitsPoolDurationMonths,
      terms.extensionOfBenefitsPoolAtIssue,
    );

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Asset Care pays from Acceleration of Benefits (AOB) first, then Continuation of Benefits (COB). Inflation accrues at policy anniversaries while each pool remains open."
        sections={[
          {
            heading: 'Acceleration of Benefits (AOB)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: baseAtIssueNote,
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            // Unlimited/Lifetime COB seeds the sentinel pool; render "Unlimited"
            // rather than the raw ~$1B balance (which drifts below the sentinel
            // once draws begin).
            unlimited: terms.isUnlimited === true,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
            note: eobAtIssueNote,
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `AOB drew ${currency(baseDrawdown)}; death benefit reduction capped at minimum guaranteed ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="For Asset Care, only AOB drawdown reduces the death benefit, in direct proportion to the remaining AOB pool. COB drawdown does not reduce death benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced this month', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when room & board is still out-of-pocket — Independent Living books
    // R&B even on non-paying months (cmTwo guards the same way); it's the exact
    // OOP this modeling exists to surface. Otherwise PaidCell shows the
    // zero-row reason natively.
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0) return null;

    const dailyCost = month.facts.dailyCareCost;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;

    // When a month draws from BOTH pools the AOB pool was exhausted mid-month,
    // so the day-split breakdown is worth showing: AOB covered some days at the
    // AOB daily cap, COB covered the rest at the (separately inflated) COB cap.
    // Back the per-pool day counts out of the drawn dollars using the same
    // effective per-day rate the engine applied — min(daily cost, pool cap) —
    // so days × rate reconciles exactly to the drawn amount.
    const bothPoolsPaid = drawnBase > 0 && drawnEob > 0;
    const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
    const baseDaily =
      dailyByEnvironment[month.context.careEnvironment] ?? terms.nominalDailyBenefitBase;
    const baseMonthly = baseDaily * DAYS_IN_MONTH;
    const policyYear = month.context.policyYear;
    const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
    const eobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
    const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
    const eobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
    const baseDailyCap =
      (baseMonthly * compoundFactor(baseRate, baseCapYears ?? 0, policyYear)) / DAYS_IN_MONTH;
    const eobDailyCap =
      (baseMonthly * compoundFactor(eobRate, eobCapYears ?? 0, policyYear)) / DAYS_IN_MONTH;
    const basePerDay = Math.min(dailyCost, baseDailyCap);
    const eobPerDay = Math.min(dailyCost, eobDailyCap);
    const aobDaysDrawn = basePerDay > 0 ? drawnBase / basePerDay : 0;
    const cobDaysDrawn = eobPerDay > 0 ? drawnEob / eobPerDay : 0;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit (subject to cap and indemnity percent) while AOB has balance, then reimburses from COB. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            reimbursementPayoutRows({ month })
          )}
        </div>
        {bothPoolsPaid ? (
          <div className="mt-1 border-t border-gray-700 pt-1">
            <div className="mb-0.5 text-[10px] text-gray-400">
              AOB pool exhausted this month — COB covers the rest:
            </div>
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              {terms.isIndemnity ? (
                // Indemnity transition row (P2-9): the AOB sliver was paid as
                // cash at the indemnity rate (cap × indemnity percent), not at
                // the reimbursement per-day rate — a day × rate derivation
                // from the reimbursement rate wouldn't reconcile, so show the
                // cash amount plainly.
                <span className="text-gray-300">AOB · cash indemnity</span>
              ) : (
                <span className="text-gray-300">
                  AOB · {aobDaysDrawn.toFixed(1)}d × {currency(basePerDay)}/day
                </span>
              )}
              <span className="font-semibold tabular-nums">{currency(drawnBase)}</span>
              <span className="text-gray-300">
                COB · {cobDaysDrawn.toFixed(1)}d × {currency(eobPerDay)}/day
              </span>
              <span className="font-semibold tabular-nums">{currency(drawnEob)}</span>
            </div>
          </div>
        ) : drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 text-[10px] text-gray-400">
            Drawn from {drawnBase > 0 ? 'AOB' : 'COB'}{' '}
            {currency(drawnBase > 0 ? drawnBase : drawnEob)}
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const surplusApplied =
      (month.deltas.strategyDeltas as AssetCareDeltas | undefined)?.indemnitySurplusApplied ?? 0;
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;
    const showWaitingPeriod = wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0;

    if (surplusApplied === 0 && !showWaitingPeriod) return null;

    return (
      <>
        {surplusApplied > 0 && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {showWaitingPeriod && (
          <div className="mt-0.5 text-[10px] text-amber-700">
            {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
          </div>
        )}
      </>
    );
  },

  // Asset Care's monthly cap uses the AOB inflation rate.
  benefitInflationRate: terms => terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Asset Care draws from Acceleration of Benefits (AOB) first, then Continuation of Benefits (COB). Inflation can accrue at policy anniversaries while pools remain open."
        formulas={[
          {
            label: 'Closing pool',
            expression: '(opening AOB + opening COB) + inflation accrual − drawn this month',
          },
          { label: 'Draw order', expression: 'AOB first; COB engages once AOB is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly cap × indemnity %, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays regardless of actual cost while AOB has balance."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this care environment and policy year, with the daily equivalent beneath. While AOB has balance the cap inflates on the AOB rate; once AOB is exhausted the draw moves to COB and the cap inflates on the COB rate."
        formulas={[
          {
            label: 'AOB phase',
            expression: (
              <>
                at-issue daily for environment × days in month ×{' '}
                <GrowthFactor
                  rate="AOB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'COB phase',
            expression: (
              <>
                at-issue daily for environment × days in month ×{' '}
                <GrowthFactor
                  rate="COB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="Each pool's inflation is capped at its configured cap year if specified. A 0% rate on a pool leaves that pool's monthly limit flat."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated AOB + COB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any inflation cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'AOB',
            expression: (
              <>
                AOB at issue ×{' '}
                <GrowthFactor
                  rate="AOB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'COB',
            expression: (
              <>
                COB at issue ×{' '}
                <GrowthFactor
                  rate="COB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          { label: 'Total', expression: 'AOB value + COB value' },
        ]}
        notes="A 0% inflation rider on a pool leaves that pool flat. Once a pool's inflation cap is reached, its value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the AOB + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (AOB depletion + COB depletion)' },
          {
            label: 'Identity',
            expression: 'Pool limit − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Asset Care reduces the death benefit in direct proportion to the remaining AOB pool as it is drawn down; COB drawdown does not reduce the death benefit."
        formulas={[
          {
            label: 'Death benefit',
            expression: 'open DB × (AOB pool after drawdown ÷ AOB pool before drawdown)',
          },
          { label: 'Floor', expression: 'max(above, minimum guaranteed amount)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
